/**
 * Ürünler read model (GET /shop/products, v0.1.70): the contract's shape, re-exported so the
 * screen imports from one place, plus the Turkish words and the number formats the cards use
 * and the address of an image's bytes. Read only.
 */

import {
  API_BASE_PATH,
  CABIN_INTERIOR_LABELS_TR,
  FILE_COVERAGE_LABELS_TR,
  PRODUCT_COVERAGE_LABELS_TR,
  PRODUCT_IMAGE_KIND_LABELS_TR,
  PRODUCT_STATUS_LABELS_TR,
  PRODUCT_TYPE_LABELS_TR,
  type ProductCard,
  type ProductImageSize,
  productImagePath,
  productVehicleLabel,
} from '@twin/contracts';

export type {
  CabinInterior,
  ProductCard,
  ProductCoverage,
  ProductFile,
  ProductGeometry,
  ProductImage,
  ProductImageKind,
  ProductImageSize,
  ProductListing,
  ProductStatus,
  ProductsView,
  ProductType,
  ProductVehicle,
} from '@twin/contracts';
export { productVehicleLabel } from '@twin/contracts';

export function productStatusLabel(status: string): string {
  return (PRODUCT_STATUS_LABELS_TR as Readonly<Record<string, string>>)[status] ?? status;
}

export function productTypeLabel(type: string): string {
  return (PRODUCT_TYPE_LABELS_TR as Readonly<Record<string, string>>)[type] ?? type;
}

export function productCoverageLabel(coverage: string): string {
  return (PRODUCT_COVERAGE_LABELS_TR as Readonly<Record<string, string>>)[coverage] ?? coverage;
}

export function cabinInteriorLabel(value: string): string {
  return (CABIN_INTERIOR_LABELS_TR as Readonly<Record<string, string>>)[value] ?? value;
}

export function fileCoverageLabel(word: string): string {
  return FILE_COVERAGE_LABELS_TR[word] ?? word.replace(/_/g, ' ');
}

export function imageKindLabel(kind: string): string {
  return (PRODUCT_IMAGE_KIND_LABELS_TR as Readonly<Record<string, string>>)[kind] ?? kind;
}

/** The URL of an image's bytes on this origin (the api serves the media volume). */
export function productImageUrl(assetId: string, size: ProductImageSize): string {
  return `${API_BASE_PATH}${productImagePath(assetId, size)}`;
}

/** "4,4 M" for 4,418,118 and "152 K" for 152,300: the triangle count a card can carry. */
export function formatTriangles(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1).replace('.', ',')} M`;
  if (count >= 10_000) return `${Math.round(count / 1_000)} K`;
  return String(count);
}

/** "221 MB" or "1,2 GB" from bytes; MB from the card's own number when bytes are unknown. */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1).replace('.', ',')} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/** "5287 x 2165 x 1752 mm": a measured extent, rounded to whole millimetres. */
export function formatExtent(extentMm: readonly [number, number, number], unit = 'mm'): string {
  return `${extentMm.map((value) => Math.round(value)).join(' x ')} ${unit}`;
}

/** Words a search box matches against: sku, title, vehicle, formats, type and coverage. */
export function productSearchText(product: ProductCard): string {
  return [
    product.sku,
    product.title,
    productVehicleLabel(product.vehicle) ?? '',
    product.formats.join(' '),
    productTypeLabel(product.productType),
    productCoverageLabel(product.coverage),
  ]
    .join(' ')
    .toLocaleLowerCase('tr-TR');
}
